import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { GetWorkPermit } from "../../../services/workPermitService";
import { DropdownPermitStatus } from "../../../services/dropdown";
import { resolveImageUrl } from "../../../utils";

interface PermitStatus {
  DataId: number;
  DataLabel: string;
  Description: string | null;
}

interface WorkPermitData {
  WorkPermitId: number;
  PermitStatus: number;
  PermitStatusDesc: string;
}

interface StatusCount {
  statusId: number;
  statusLabel: string;
  count: number;
}

export default function WorkPermitSection() {
  const INITIAL_DUMMY_STATUSES: StatusCount[] = [
    { statusId: 1, statusLabel: "Waiting TR", count: 0 },
    { statusId: 2, statusLabel: "Waiting OPS", count: 0 },
    { statusId: 3, statusLabel: "Approved", count: 0 },
    { statusId: 4, statusLabel: "In Progress", count: 0 },
    { statusId: 5, statusLabel: "Done", count: 0 },
    { statusId: 6, statusLabel: "Reject", count: 0 },
  ];

  const [statusCounts, setStatusCounts] = useState<StatusCount[]>(INITIAL_DUMMY_STATUSES);
  const [loading, setLoading] = useState(false);

  const [workPermitId, ] = useState<number>(0);
  const [permitTypeId, ] = useState<number>(0);
  const [permitStatus, ] = useState<number>(0);
  const [page, ] = useState<number>(1);
  const [number, ] = useState<number>(0);

  useEffect(() => {
    const fetchPermitData = async () => {
      try {
        const mallid = parseInt(localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id") || "0");
        
        if (!mallid || mallid === 0) {
          const statusRes = await DropdownPermitStatus(0);
          
          if (statusRes?.dropdownClassDatas) {
            const counts: StatusCount[] = statusRes.dropdownClassDatas
              .filter((status: PermitStatus) => status.DataId !== 50)
              .map((status: PermitStatus) => ({
                statusId: status.DataId,
                statusLabel: status.DataLabel,
                count: 0
              }))
              .slice(0, 6);
            
            if (counts.length > 0) {
              setStatusCounts(counts);
            }
          }
          return;
        }
        
        const [statusRes, permitRes] = await Promise.all([
          DropdownPermitStatus(0),
          GetWorkPermit(workPermitId, permitTypeId, permitStatus, mallid, page, number)
        ]);
        
        if (statusRes?.dropdownClassDatas && permitRes?.workPermitHeaderDatas) {
          const workPermits: WorkPermitData[] = [];
          permitRes.workPermitHeaderDatas.forEach((header) => {
            if (header.workPermitDatas) {
              header.workPermitDatas.forEach((permit) => {
                workPermits.push(permit);
              });
            }
          });
          
          const counts: StatusCount[] = statusRes.dropdownClassDatas
            .filter((status: PermitStatus) => status.DataId !== 50)
            .map((status: PermitStatus) => {
              const count = workPermits.filter(
                (permit) => permit.PermitStatus === status.DataId
              ).length;
              
              return {
                statusId: status.DataId,
                statusLabel: status.DataLabel,
                count: count
              };
            })
            .slice(0, 6);
          
          if (counts.length > 0) {
            setStatusCounts(counts);
          }
        }
      } catch (error) {
        console.error("Error fetching permit data:", error);
      }
    };

    fetchPermitData();
  }, [workPermitId, permitTypeId, permitStatus, page, number]);

  const getStatusIcon = (label: string) => {
    const lowerLabel = label.toLowerCase();
    
    if (lowerLabel.includes('approved by tr') || lowerLabel.includes('tr')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    } 
    else if (lowerLabel.includes('approved by ops') || lowerLabel.includes('ops')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    }
    else if (lowerLabel.includes('approved') && !lowerLabel.includes('by')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    else if (lowerLabel.includes('reject')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    else if (lowerLabel.includes('cancel')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      );
    }
    else if (lowerLabel.includes('progress') || lowerLabel.includes('ongoing') || lowerLabel.includes('new')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    else if (lowerLabel.includes('done') || lowerLabel.includes('complete')) {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    else {
      return (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
  };

  return (
    <div className="relative rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 overflow-hidden dark:bg-gray-900 dark:ring-gray-700 min-h-44 pb-8">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Work Permit</h3>
        <Link to="/workPermit" className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-white flex items-center gap-1">
          View all <span>{"\u203A"}</span>
        </Link>
      </div>

      <div className="relative z-10 pr-20">
        <div className="grid grid-cols-3 gap-x-4 gap-y-4">
          {statusCounts.map((status) => (
            <div key={status.statusId} className="flex flex-col items-center text-center gap-1">
              <div className="w-10 h-10 rounded-lg border border-blue-100 bg-blue-50 dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                {getStatusIcon(status.statusLabel)}
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">{status.statusLabel}</p>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">{status.count}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-2 right-2 w-28 h-28 pointer-events-none opacity-40 sm:opacity-100">
        <img 
          src={resolveImageUrl("/images/icon (6).png")} 
          alt="Work tools" 
          className="w-full h-full object-contain"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      </div>
    </div>
  );
}